// src/services/placesService.js
import {
  getPlaces,
  addPlace,
  updatePlace,
  deletePlace,
  getPlaceById,
  getAccommodation,
  getAccommodationById,
  getTravel,
  eventBridgeService,
  EVENT_TYPES,
  removeDocumentReferenceBlocks,
  updateDocumentReferenceBlocks,
  linkRegistryConfigService,
  connectionRegistryService
} from '@trip-planner-monorepo/shared';
import { db, doc, getDoc, updateDoc } from '@trip-planner-monorepo/shared/firebase/firebaseInit';

/**
 * Calculates the Levenshtein distance between two strings
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} The Levenshtein distance
 */
function levenshteinDistance(a, b) {
  const matrix = [];
  const aLen = a.length;
  const bLen = b.length;

  // Initialize first row and column
  for (let i = 0; i <= bLen; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= aLen; j++) {
    matrix[0][j] = j;
  }

  // Fill the matrix
  for (let i = 1; i <= bLen; i++) {
    for (let j = 1; j <= aLen; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[bLen][aLen];
}

/**
 * Calculates similarity score between two place names
 * @param {string} target - Target place name to match against
 * @param {string} candidate - Candidate place name to compare
 * @returns {number} Similarity score between 0 and 1 (1 being exact match)
 */
function calculateSimilarityScore(target, candidate) {
  // Normalize strings for comparison (lowercase, trim, remove accents)
  const normalizeString = (str) => {
    return str.toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics/accents
  };
  
  const normalizedTarget = normalizeString(target);
  const normalizedCandidate = normalizeString(candidate);
  
  // Exact match gets perfect score
  if (normalizedTarget === normalizedCandidate) {
    return 1.0;
  }
  
  // Calculate various similarity metrics
  const maxLength = Math.max(normalizedTarget.length, normalizedCandidate.length);
  const minLength = Math.min(normalizedTarget.length, normalizedCandidate.length);
  const levenshtein = levenshteinDistance(normalizedTarget, normalizedCandidate);
  
  // Improved Levenshtein-based similarity with better scaling
  let levenshteinSimilarity = Math.max(0, 1 - (levenshtein / maxLength));
  
  // Boost similarity for small differences in short strings
  if (minLength <= 6 && levenshtein <= 2) {
    levenshteinSimilarity = Math.max(levenshteinSimilarity, 0.7 - (levenshtein * 0.15));
  }
  
  // Check for substring matches (partial contains)
  const substringBonus = normalizedTarget.includes(normalizedCandidate) || 
                        normalizedCandidate.includes(normalizedTarget) ? 0.2 : 0;
  
  // Check for word-based matches (splitting on spaces, commas, etc.)
  const targetWords = normalizedTarget.split(/[\s,]+/).filter(word => word.length > 0);
  const candidateWords = normalizedCandidate.split(/[\s,]+/).filter(word => word.length > 0);
  
  let wordMatches = 0;
  const totalWords = Math.max(targetWords.length, candidateWords.length);
  
  targetWords.forEach(targetWord => {
    if (candidateWords.some(candidateWord => {
      // Exact word match
      if (candidateWord === targetWord) return true;
      // Partial word match (for abbreviations)
      if (candidateWord.includes(targetWord) || targetWord.includes(candidateWord)) return true;
      // Fuzzy word match (allow 1-2 character differences for words)
      const wordDistance = levenshteinDistance(targetWord, candidateWord);
      if (targetWord.length <= 4 && candidateWord.length <= 4) {
        return wordDistance <= 1;
      } else if (targetWord.length <= 8 && candidateWord.length <= 8) {
        return wordDistance <= 2;
      }
      return wordDistance <= Math.floor(Math.max(targetWord.length, candidateWord.length) * 0.3);
    })) {
      wordMatches++;
    }
  });
  
  const wordSimilarity = totalWords > 0 ? (wordMatches / totalWords) : 0;
  
  // Combine metrics with adjusted weights for better fuzzy matching
  const combinedScore = (
    levenshteinSimilarity * 0.7 +  // 70% weight to character-level similarity
    wordSimilarity * 0.2 +         // 20% weight to word-level similarity
    substringBonus * 0.1            // 10% bonus for substring matches
  );
  
  // Ensure score is between 0 and 1
  return Math.min(Math.max(combinedScore, 0), 1);
}

export const placesService = {
  async fetchPlaces(userId, tripId) {
    try {
      const places = await getPlaces(userId, tripId);
      // Handle null or undefined places array
      if (!places || !Array.isArray(places)) {
        return [];
      }
      return places.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error fetching places:', error);
      throw error;
    }
  },

  async searchPlaces(searchTerm) {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}`
      );
      const results = await response.json();
      
      return results.map(place => ({
        id: place.place_id,
        name: place.display_name.split(',')[0],
        fullName: place.display_name,
        latitude: parseFloat(place.lat),
        longitude: parseFloat(place.lon),
        type: place.type,
        importance: place.importance,
        context: place.display_name.split(',').slice(1).join(',').trim()
      }));
    } catch (error) {
      console.error('Error searching places:', error);
      throw error;
    }
  },

  async createPlace(userId, tripId, placeData) {
    try {
      const newPlace = {
        name: placeData.name,
        fullName: placeData.fullName || placeData.name,
        context: placeData.context || '',
        latitude: placeData.latitude || null,
        longitude: placeData.longitude || null,
        type: placeData.type || '',
        userContent: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      return await addPlace(userId, tripId, newPlace);
    } catch (error) {
      console.error('Error creating place:', error);
      throw error;
    }
  },

  async updatePlace(userId, tripId, placeId, placeData) {
    try {
      const updateData = {
        ...placeData,
        updatedAt: new Date().toISOString()
      };
      
      await updatePlace(userId, tripId, placeId, updateData);
      return this.fetchPlaces(userId, tripId);
    } catch (error) {
      console.error('Error updating place:', error);
      throw error;
    }
  },

  async deletePlace(userId, tripId, placeId) {
    try {
      const placeToDelete = await getPlaceById(userId, tripId, placeId);
      await deletePlace(userId, tripId, placeId);

      if (placeToDelete) {
        eventBridgeService.publish(EVENT_TYPES.PLACE_DELETED, {
          userId,
          tripId,
          id: placeId,
          placeId,
          name: placeToDelete.name,
          timestamp: new Date().toISOString()
        });
      }

      return this.fetchPlaces(userId, tripId);
    } catch (error) {
      console.error('Error deleting place:', error);
      throw error;
    }
  },

  validatePlaceData(placeData) {
    const errors = [];
    
    if (!placeData.name || placeData.name.trim().length === 0) {
      errors.push('Place name is required');
    }

    if (placeData.latitude && (isNaN(placeData.latitude) || 
        placeData.latitude < -90 || placeData.latitude > 90)) {
      errors.push('Invalid latitude value');
    }

    if (placeData.longitude && (isNaN(placeData.longitude) || 
        placeData.longitude < -180 || placeData.longitude > 180)) {
      errors.push('Invalid longitude value');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  validateResourceData(data) {
    const errors = [];
    
    if (!data.name?.trim()) {
      errors.push('Resource name is required');
    }
    
    if (!data.url?.trim()) {
      errors.push('Resource URL is required');
    } else {
      try {
        new URL(data.url);
      } catch {
        errors.push('Please enter a valid URL');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  async addResource(userId, tripId, placeId, resourceData) {
    try {
      const validation = this.validateResourceData(resourceData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
      const place = await getDoc(placeRef);
      
      if (!place.exists()) {
        throw new Error('Place not found');
      }

      const currentResources = place.data().resources || [];
      const newResource = {
        id: Date.now().toString(),
        name: resourceData.name.trim(),
        url: resourceData.url.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(placeRef, {
        resources: [...currentResources, newResource]
      });

      return newResource;
    } catch (error) {
      console.error('Error adding resource:', error);
      throw error;
    }
  },

  async updateResource(userId, tripId, placeId, resourceId, resourceData) {
    try {
      const validation = this.validateResourceData(resourceData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
      const place = await getDoc(placeRef);
      
      if (!place.exists()) {
        throw new Error('Place not found');
      }

      const resources = place.data().resources || [];
      const resourceIndex = resources.findIndex(r => r.id === resourceId);
      
      if (resourceIndex === -1) {
        throw new Error('Resource not found');
      }

      resources[resourceIndex] = {
        ...resources[resourceIndex],
        name: resourceData.name.trim(),
        url: resourceData.url.trim(),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(placeRef, { resources });
      return resources[resourceIndex];
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  },

  async deleteResource(userId, tripId, placeId, resourceId) {
    try {
      const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
      const place = await getDoc(placeRef);
      
      if (!place.exists()) {
        throw new Error('Place not found');
      }

      const resources = place.data().resources || [];
      const updatedResources = resources.filter(r => r.id !== resourceId);

      if (resources.length === updatedResources.length) {
        throw new Error('Resource not found');
      }

      await updateDoc(placeRef, { resources: updatedResources });
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  },

  /**
   * Finds places with names similar to the given place name
   * @param {string} userId - The user ID
   * @param {string} tripId - The trip ID
   * @param {string} placeName - The place name to find similar matches for
   * @param {number} threshold - Similarity threshold (0-1, default 0.6)
   * @returns {Promise<Array>} Array of similar places with similarity scores
   */
  async findSimilarPlaces(userId, tripId, placeName, threshold = 0.6) {
    try {
      // Input validation
      if (!userId || !tripId || !placeName) {
        throw new Error('userId, tripId, and placeName are required');
      }
      
      if (typeof threshold !== 'number' || threshold < 0 || threshold > 1) {
        throw new Error('threshold must be a number between 0 and 1');
      }
      
      // Fetch all places for the trip
      const allPlaces = await this.fetchPlaces(userId, tripId);
      
      if (!allPlaces || allPlaces.length === 0) {
        return [];
      }
      
      // Calculate similarity scores for each place
      const placesWithScores = allPlaces.map(place => {
        // Calculate similarity against both name and fullName
        const nameScore = calculateSimilarityScore(placeName, place.name);
        const fullNameScore = place.fullName ? 
          calculateSimilarityScore(placeName, place.fullName) : 0;
        
        // Use the higher of the two scores
        const similarityScore = Math.max(nameScore, fullNameScore);
        
        return {
          ...place,
          similarityScore: Math.round(similarityScore * 1000) / 1000 // Round to 3 decimal places
        };
      });
      
      // Filter by threshold and sort by similarity score (highest first)
      const similarPlaces = placesWithScores
        .filter(place => place.similarityScore >= threshold)
        .sort((a, b) => b.similarityScore - a.similarityScore);
      
      console.log(`Found ${similarPlaces.length} similar places for "${placeName}" with threshold ${threshold}`);
      
      return similarPlaces;
      
    } catch (error) {
      console.error('Error finding similar places:', error);
      throw error;
    }
  },

  /**
   * Analyzes place usage and returns count of references
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID  
   * @param {string} placeId - Place ID to analyze
   * @returns {Promise<number>} Number of references to this place
   */
  async getPlaceUsageCount(userId, tripId, placeId) {
    try {
      const references = await this.getPlaceReferences(userId, tripId, placeId);
      return references.totalCount;
    } catch (error) {
      console.error('Error getting place usage count:', error);
      throw error;
    }
  },

  /**
   * Gets detailed references to a place across all entity types
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} placeId - Place ID to analyze
   * @returns {Promise<Object>} Object containing arrays of referencing entities
   */
  async getPlaceReferences(userId, tripId, placeId) {
    try {
      const references = {
        accommodations: [],
        travel: [],
        registryLinks: [],
        // Future: activities, notes, etc.
        totalCount: 0
      };

      // Check accommodation references
      // Note: Uses getAccommodation from shared services
      try {
        const accommodations = await getAccommodation(userId, tripId);
        
        if (accommodations && Array.isArray(accommodations)) {
          const referencingAccommodations = accommodations.filter(accommodation => {
            // Handle both old simple structure (placeReference: 'place-id') 
            // and new nested structure (placeReference: { placeId: 'place-id' })
            if (typeof accommodation.placeReference === 'string') {
              return accommodation.placeReference === placeId;
            } else if (accommodation.placeReference && typeof accommodation.placeReference === 'object') {
              return accommodation.placeReference.placeId === placeId;
            }
            return false;
          });
          
          references.accommodations = referencingAccommodations.map(acc => ({
            id: acc.id,
            type: 'accommodation',
            name: acc.summary || acc.details?.name || 'Unnamed accommodation',
            details: acc.details,
            linkedAt: acc.placeReference ? acc.updatedAt : null
          }));
        }
      } catch (error) {
        console.warn('Could not check accommodation references:', error.message);
        // Continue without accommodation references
      }

      // Check travel references
      // Note: Uses getTravel from shared services
      try {
        const travelItems = await getTravel(userId, tripId);
        
        if (travelItems && Array.isArray(travelItems)) {
          const referencingTravel = travelItems.filter(travel => 
            travel.departurePlace === placeId || travel.arrivalPlace === placeId
          );
          
          references.travel = referencingTravel.map(travel => ({
            id: travel.id,
            type: 'travel',
            name: travel.summary || `${travel.type} travel`,
            referenceType: travel.departurePlace === placeId ? 'departure' : 'arrival',
            details: travel.details,
            linkedAt: travel.updatedAt
          }));
        }
      } catch (error) {
        console.warn('Could not check travel references:', error.message);
        // Continue without travel references
      }

      // Registry-aware (Issue 16): count active registry links of any type touching
      // this place. Legacy scans above only see `placeReference` / travel
      // `departurePlace`/`arrivalPlace` — on registry-only trips those fields are
      // absent, so a heavily-used place looked orphaned and usage counts were wrong.
      try {
        const links = await connectionRegistryService.getLinksForEntity(userId, tripId, 'place', placeId);
        const alreadyCountedIds = new Set([
          ...references.accommodations.map((ref) => ref.id),
          ...references.travel.map((ref) => ref.id)
        ]);
        references.registryLinks = links
          .filter((link) => (link.linkLifecycle ?? 'active') === 'active')
          .map((link) => (link.entityLow?.type === 'place' ? link.entityHigh : link.entityLow))
          .filter((other) => other?.id && !alreadyCountedIds.has(other.id));
      } catch (error) {
        console.warn('Could not check registry links:', error.message);
      }

      // Calculate total count
      references.totalCount = references.accommodations.length + references.travel.length + references.registryLinks.length;

      return references;
    } catch (error) {
      console.error('Error getting place references:', error);
      throw error;
    }
  },

  /**
   * Identifies places with zero references (orphaned places)
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @returns {Promise<Array>} Array of place objects that have no references
   */
  async getOrphanedPlaces(userId, tripId) {
    try {
      const places = await this.fetchPlaces(userId, tripId);
      const orphanedPlaces = [];

      for (const place of places) {
        const usageCount = await this.getPlaceUsageCount(userId, tripId, place.id);
        if (usageCount === 0) {
          orphanedPlaces.push({
            ...place,
            usageCount: 0,
            hasUserContent: !!(place.userContent && place.userContent.trim().length > 0),
            hasCustomResources: !!(place.resources && place.resources.length > 0)
          });
        }
      }

      return orphanedPlaces;
    } catch (error) {
      console.error('Error finding orphaned places:', error);
      throw error;
    }
  },

  /**
   * Analyzes usage patterns for all places in a trip
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @returns {Promise<Object>} Usage analysis summary
   */
  async analyzePlaceUsage(userId, tripId) {
    try {
      const places = await this.fetchPlaces(userId, tripId);
      const usageAnalysis = {
        totalPlaces: places.length,
        referencedPlaces: 0,
        orphanedPlaces: 0,
        averageReferences: 0,
        placesByUsage: {
          zero: [],
          low: [], // 1-2 references
          medium: [], // 3-5 references  
          high: [] // 6+ references
        },
        summary: {
          mostReferenced: null,
          orphanedWithContent: 0,
          potentialDuplicates: []
        }
      };

      let totalReferences = 0;
      let maxReferences = 0;
      let mostReferencedPlace = null;

      for (const place of places) {
        const references = await this.getPlaceReferences(userId, tripId, place.id);
        const count = references.totalCount;
        
        totalReferences += count;
        
        if (count > maxReferences) {
          maxReferences = count;
          mostReferencedPlace = { ...place, referenceCount: count };
        }

        const placeWithUsage = {
          ...place,
          referenceCount: count,
          references: references,
          hasUserContent: !!(place.userContent && place.userContent.trim().length > 0)
        };

        // Categorize by usage
        if (count === 0) {
          usageAnalysis.orphanedPlaces++;
          usageAnalysis.placesByUsage.zero.push(placeWithUsage);
          if (placeWithUsage.hasUserContent || (place.resources && place.resources.length > 0)) {
            usageAnalysis.summary.orphanedWithContent++;
          }
        } else if (count <= 2) {
          usageAnalysis.referencedPlaces++;
          usageAnalysis.placesByUsage.low.push(placeWithUsage);
        } else if (count <= 5) {
          usageAnalysis.referencedPlaces++;
          usageAnalysis.placesByUsage.medium.push(placeWithUsage);
        } else {
          usageAnalysis.referencedPlaces++;
          usageAnalysis.placesByUsage.high.push(placeWithUsage);
        }
      }

      usageAnalysis.averageReferences = places.length > 0 ? (totalReferences / places.length) : 0;
      usageAnalysis.summary.mostReferenced = mostReferencedPlace;

      return usageAnalysis;
    } catch (error) {
      console.error('Error analyzing place usage:', error);
      throw error;
    }
  },

  /**
   * Adds a content block to a place record
   * Content blocks track relationships between places and other entities (accommodations, tours, etc.)
   * that link places to specific days with contextual information.
   * 
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID  
   * @param {string} placeId - Place ID
   * @param {Object} contentBlock - Content block data with sourceEntityId, sourceEntityType, sourceEntityName, relatedDayIds
   * @returns {Promise<Object>} Updated place
   */
  async addContentBlock(userId, tripId, placeId, contentBlock) {
    try {
      console.log(`Adding content block to place ${placeId}:`, contentBlock);
      
      const place = await getPlaceById(userId, tripId, placeId);
      if (!place) throw new Error('Place not found');

      // Create new content block with timestamps
      const newContentBlock = {
        ...contentBlock,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add to existing contentBlocks array or create new array
      const updatedContentBlocks = [...(place.contentBlocks || []), newContentBlock];
      
      const updatedPlace = {
        ...place,
        contentBlocks: updatedContentBlocks,
        contentBlocksLastUpdated: new Date().toISOString()
      };

      await updatePlace(userId, tripId, placeId, updatedPlace);
      
      console.log(`Successfully added content block to place ${placeId}`);
      return updatedPlace;
    } catch (error) {
      console.error('Error adding content block to place:', error);
      throw error;
    }
  },

  /**
   * Removes a content block from a place record by source entity ID
   * Used when an accommodation is unlinked from a place or deleted
   * 
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} placeId - Place ID  
   * @param {string} sourceEntityId - Source entity ID to remove (e.g., accommodation ID)
   * @returns {Promise<Object>} Updated place
   */
  async removeContentBlock(userId, tripId, placeId, sourceEntityId) {
    try {
      console.log(`Removing content block from place ${placeId} for source entity ${sourceEntityId}`);
      
      const place = await getPlaceById(userId, tripId, placeId);
      if (!place) throw new Error('Place not found');

      // Filter out content blocks for the specified source entity
      const updatedContentBlocks = (place.contentBlocks || []).filter(
        block => block.sourceEntityId !== sourceEntityId
      );

      const updatedPlace = {
        ...place,
        contentBlocks: updatedContentBlocks,
        contentBlocksLastUpdated: new Date().toISOString()
      };

      await updatePlace(userId, tripId, placeId, updatedPlace);
      
      console.log(`Successfully removed content block from place ${placeId}`);
      return updatedPlace;
    } catch (error) {
      console.error('Error removing content block from place:', error);
      throw error;
    }
  },

  /**
   * Updates a content block in a place record
   * Used when accommodation details change (name, dates, etc.)
   * 
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} placeId - Place ID
   * @param {string} sourceEntityId - Source entity ID to update
   * @param {Object} updatedData - Updated content block data
   * @returns {Promise<Object>} Updated place
   */
  async updateContentBlock(userId, tripId, placeId, sourceEntityId, updatedData) {
    try {
      console.log(`Updating content block in place ${placeId} for source entity ${sourceEntityId}:`, updatedData);
      
      const place = await getPlaceById(userId, tripId, placeId);
      if (!place) throw new Error('Place not found');

      // Update the specific content block
      const updatedContentBlocks = (place.contentBlocks || []).map(block => 
        block.sourceEntityId === sourceEntityId 
          ? { ...block, ...updatedData, updatedAt: new Date().toISOString() }
          : block
      );

      const updatedPlace = {
        ...place,
        contentBlocks: updatedContentBlocks,
        contentBlocksLastUpdated: new Date().toISOString()
      };

      await updatePlace(userId, tripId, placeId, updatedPlace);
      
      console.log(`Successfully updated content block in place ${placeId}`);
      return updatedPlace;
    } catch (error) {
      console.error('Error updating content block in place:', error);
      throw error;
    }
  },

  /**
   * Gets all content blocks for a place
   * Returns the content blocks that define relationships between this place and other entities
   * 
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} placeId - Place ID
   * @returns {Promise<Array>} Array of content blocks
   */
  async getContentBlocks(userId, tripId, placeId) {
    try {
      const place = await getPlaceById(userId, tripId, placeId);
      return place?.contentBlocks || [];
    } catch (error) {
      console.error('Error getting content blocks for place:', error);
      throw error;
    }
  },

  /**
   * Gets content blocks for a specific source entity type
   * Useful for filtering content blocks by entity type (accommodation, tour, etc.)
   * 
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} placeId - Place ID
   * @param {string} sourceEntityType - Type of source entity to filter by
   * @returns {Promise<Array>} Array of content blocks for the specified entity type
   */
  async getContentBlocksByType(userId, tripId, placeId, sourceEntityType) {
    try {
      const contentBlocks = await this.getContentBlocks(userId, tripId, placeId);
      return contentBlocks.filter(block => block.sourceEntityType === sourceEntityType);
    } catch (error) {
      console.error('Error getting content blocks by type:', error);
      throw error;
    }
  },

  /**
   * Validates content block data against the schema
   * Ensures content blocks have required fields and valid data types
   * 
   * @param {Object} contentBlock - Content block data to validate
   * @returns {Object} Validation result with isValid boolean and errors array
   */
  validateContentBlockData(contentBlock) {
    const errors = [];

    // Check required fields
    if (!contentBlock.sourceEntityId) {
      errors.push('sourceEntityId is required');
    }
    if (!contentBlock.sourceEntityType) {
      errors.push('sourceEntityType is required');
    }
    if (!contentBlock.sourceEntityName) {
      errors.push('sourceEntityName is required');
    }
    if (!contentBlock.relatedDayIds || !Array.isArray(contentBlock.relatedDayIds) || contentBlock.relatedDayIds.length === 0) {
      errors.push('relatedDayIds must be a non-empty array');
    }

    // Check data types and values
    if (contentBlock.sourceEntityName && contentBlock.sourceEntityName.length > 200) {
      errors.push('sourceEntityName must be 200 characters or less');
    }

    // Check date range if provided
    if (contentBlock.dateRange) {
      if (!contentBlock.dateRange.start) {
        errors.push('dateRange.start is required if dateRange is provided');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Synchronize place contentBlocks with accommodations that reference this place
   * This is a utility method to fix cases where accommodations have placeReference 
   * but the place doesn't have corresponding contentBlocks
   * 
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} placeId - Place ID to sync
   * @returns {Promise<Object>} Updated place with synchronized contentBlocks
   */
  async syncPlaceContentBlocks(userId, tripId, placeId) {
    try {
      console.log(`🔄 Syncing contentBlocks for place ${placeId}`);
      
      // Get the place
      const place = await getPlaceById(userId, tripId, placeId);
      if (!place) throw new Error('Place not found');
      
      // Get all accommodations that reference this place
      const accommodations = await getAccommodation(userId, tripId);
      
      if (!accommodations || !Array.isArray(accommodations)) {
        console.log('🔄 No accommodations found for sync');
        return place;
      }
      
      const linkedAccommodations = accommodations.filter(accommodation => {
        if (typeof accommodation.placeReference === 'string') {
          return accommodation.placeReference === placeId;
        } else if (accommodation.placeReference && typeof accommodation.placeReference === 'object') {
          return accommodation.placeReference.placeId === placeId;
        }
        return false;
      });
      
      console.log(`🔄 Found ${linkedAccommodations.length} accommodations linked to place ${placeId}`);
      
      if (linkedAccommodations.length === 0) {
        console.log('🔄 No linked accommodations to sync');
        return place;
      }
      
      // Create contentBlocks for each linked accommodation
      const existingContentBlocks = place.contentBlocks || [];
      const newContentBlocks = [...existingContentBlocks];
      
      for (const accommodation of linkedAccommodations) {
        // Check if contentBlock already exists for this accommodation
        const existingBlock = existingContentBlocks.find(
          block => block.sourceEntityId === accommodation.id && block.sourceEntityType === 'accommodation'
        );
        
        if (!existingBlock) {
          console.log(`🔄 Creating missing contentBlock for accommodation ${accommodation.id}`);
          
          // Extract day IDs from accommodation's contentBlocks
          const dayIds = (accommodation.contentBlocks || []).map(block => block.dayId).filter(Boolean);
          
          const contentBlock = {
            sourceEntityId: accommodation.id,
            sourceEntityType: 'accommodation',
            sourceEntityName: accommodation.details?.name || accommodation.summary || 'Accommodation',
            relatedDayIds: dayIds,
            context: 'staying',
            dateRange: {
              start: accommodation.details?.checkInDate,
              end: accommodation.details?.checkOutDate
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          newContentBlocks.push(contentBlock);
        }
      }
      
      // Update the place if new contentBlocks were added
      if (newContentBlocks.length > existingContentBlocks.length) {
        const updatedPlace = {
          ...place,
          contentBlocks: newContentBlocks,
          contentBlocksLastUpdated: new Date().toISOString()
        };
        
        await updatePlace(userId, tripId, placeId, updatedPlace);
        console.log(`🔄 Synced place ${placeId} with ${newContentBlocks.length - existingContentBlocks.length} new contentBlocks`);
        
        return updatedPlace;
      } else {
        console.log('🔄 Place contentBlocks are already in sync');
        return place;
      }
      
    } catch (error) {
      console.error('🔄 Error syncing place contentBlocks:', error);
      throw error;
    }
  },

  /**
   * Initialize event subscriptions for the places service
   * This must be called after module initialization to avoid circular dependencies
   */
  init() {
    console.log('Initializing places service event subscriptions...');

    // Write the legacy place inverse block unless the trip is registry_only.
    // legacy_only and dual_write must still write it; only registry_only suppresses it.
    // Defensive: if the registry config can't be read (e.g. unmigrated trip or test
    // context where the config service is unavailable), default to writing legacy —
    // the backward-compatible behaviour.
    const shouldWriteLegacyForTrip = async (userId, tripId) => {
      try {
        const config = await linkRegistryConfigService?.getLinkRegistryConfig?.(userId, tripId);
        return config?.writeMode !== 'registry_only';
      } catch (error) {
        console.warn('placesService: could not read link registry config; defaulting to legacy write', error);
        return true;
      }
    };
    
    // Subscribe to key events for automatic updates during accommodation ops
    eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_PLACE_LINKED, async (eventData) => {
      const { userId, tripId, placeId, accommodationId, dayIds, accommodationName } = eventData;
      
      if (placeId && accommodationId) {
        try {
          if (!await shouldWriteLegacyForTrip(userId, tripId)) {
            console.log(`Skipping legacy place inverse block for ${placeId} (registry_only write mode)`);
            return;
          }

          console.log(`Processing ACCOMMODATION_PLACE_LINKED event for place ${placeId} and accommodation ${accommodationId}`);
          
          // Get accommodation details for content block
          const accommodation = await getAccommodationById(userId, tripId, accommodationId);
          
          if (accommodation) {
            // Create content block data with accommodation details
            // Note: dayIds may be empty initially as contentBlocks are created later in the suggestion process
            const contentBlock = {
              sourceEntityId: accommodationId,
              sourceEntityType: 'accommodation',
              sourceEntityName: accommodation.details?.name || accommodation.summary || 'Accommodation',
              relatedDayIds: dayIds || [],
              context: 'staying', // Default context for accommodation relationships
              dateRange: {
                start: accommodation.details?.checkInDate,
                end: accommodation.details?.checkOutDate
              }
            };

            // Add to place's content blocks
            await this.addContentBlock(userId, tripId, placeId, contentBlock);
            
            console.log(`Successfully added content block for accommodation ${accommodationId} to place ${placeId}`);
          }
        } catch (error) {
          console.error('Error processing accommodation place linked event:', error);
        }
      }
    });

    eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_PLACE_UNLINKED, async (eventData) => {
      const { userId, tripId, placeId, accommodationId } = eventData;
      
      if (placeId && accommodationId) {
        try {
          if (!await shouldWriteLegacyForTrip(userId, tripId)) {
            console.log(`Skipping legacy place inverse block removal for ${placeId} (registry_only write mode)`);
            return;
          }

          console.log(`Processing ACCOMMODATION_PLACE_UNLINKED event for place ${placeId} and accommodation ${accommodationId}`);
          
          // Remove content block from place
          await this.removeContentBlock(userId, tripId, placeId, accommodationId);
          
          console.log(`Successfully removed content block for accommodation ${accommodationId} from place ${placeId}`);
        } catch (error) {
          console.error('Error processing accommodation place unlinked event:', error);
        }
      }
    });

    eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_CONTENT_BLOCKS_UPDATED, async (eventData) => {
      const { userId, tripId, accommodationId, contentBlocks, placeReference } = eventData;
      
      if (placeReference && placeReference.placeId && accommodationId) {
        try {
          if (!await shouldWriteLegacyForTrip(userId, tripId)) {
            console.log(`Skipping legacy place inverse block update for ${placeReference.placeId} (registry_only write mode)`);
            return;
          }

          console.log(`Processing ACCOMMODATION_CONTENT_BLOCKS_UPDATED event for place ${placeReference.placeId} and accommodation ${accommodationId}`);
          
          // Get the updated accommodation details
          const accommodation = await getAccommodationById(userId, tripId, accommodationId);
          
          if (accommodation) {
            // Extract day IDs from content blocks
            const dayIds = contentBlocks
              .filter(block => block.dayId)
              .map(block => block.dayId);
            
            // Update the content block in the place with new day associations
            const updatedContentBlock = {
              sourceEntityId: accommodationId,
              sourceEntityType: 'accommodation',
              sourceEntityName: accommodation.details?.name || accommodation.summary || 'Accommodation',
              relatedDayIds: dayIds,
              context: 'staying',
              dateRange: {
                start: accommodation.details?.checkInDate,
                end: accommodation.details?.checkOutDate
              }
            };
            
            await this.updateContentBlock(userId, tripId, placeReference.placeId, accommodationId, updatedContentBlock);
            
            console.log(`Successfully updated content block for accommodation ${accommodationId} in place ${placeReference.placeId} with ${dayIds.length} day associations`);
          }
        } catch (error) {
          console.error('Error processing accommodation content blocks updated event:', error);
        }
      }
    });

    eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_DELETED, async (eventData) => {
      const { userId, tripId, placeReference } = eventData;
      const accommodationId = eventData.accommodationId || eventData.id;
      
      if (placeReference && placeReference.placeId) {
        try {
          console.log(`Processing ACCOMMODATION_DELETED event for place ${placeReference.placeId} and accommodation ${accommodationId}`);
          
          // Remove content block from place
          await this.removeContentBlock(userId, tripId, placeReference.placeId, accommodationId);
          
          console.log(`Successfully removed content block for deleted accommodation ${accommodationId} from place ${placeReference.placeId}`);
        } catch (error) {
          console.error('Error processing accommodation deletion event:', error);
        }
      }
    });

    // Subscribe to document updated events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_UPDATED, async (eventData) => {
      const { userId, tripId, documentId, updateData } = eventData;
      console.log('📄 [PlacesService] handleDocumentUpdated: Processing document update', {
        userId,
        tripId,
        documentId,
        hasUpdateData: !!updateData
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [PlacesService] handleDocumentUpdated: Missing required event data');
        return;
      }

      try {
        // Get all places for the trip
        const places = await getPlaces(userId, tripId);
        if (!places || places.length === 0) {
          console.log('📍 [PlacesService] handleDocumentUpdated: No places found for trip');
          return;
        }

        // Process each place to update document references
        for (const place of places) {
          if (!place.contentBlocks || place.contentBlocks.length === 0) continue;

          // Check if this place has document references for the updated document
          const hasDocumentReference = place.contentBlocks.some(
            block => block.type === 'document_reference' && block.documentId === documentId
          );

          if (hasDocumentReference) {
            console.log('🔄 [PlacesService] handleDocumentUpdated: Updating document references in place', {
              placeId: place.id,
              documentId
            });

            // Create updated document object for the utility function
            const updatedDocument = {
              id: documentId,
              ...updateData
            };

            // Update document reference blocks
            const updatedContentBlocks = updateDocumentReferenceBlocks(place.contentBlocks, updatedDocument);
            
            // Update the place with new content blocks
            await updatePlace(userId, tripId, place.id, {
              contentBlocks: updatedContentBlocks,
              contentBlocksLastUpdated: new Date().toISOString()
            });
            
            console.log('✅ [PlacesService] handleDocumentUpdated: Successfully updated document references in place', {
              placeId: place.id,
              documentId
            });
          }
        }
      } catch (error) {
        console.error('❌ [PlacesService] handleDocumentUpdated: Error updating document references', {
          error: error.message,
          stack: error.stack
        });
      }
    });

    // Subscribe to document deleted events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_DELETED, async (eventData) => {
      const { userId, tripId, documentId } = eventData;
      console.log('🗑️ [PlacesService] handleDocumentDeleted: Processing document deletion', {
        userId,
        tripId,
        documentId
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [PlacesService] handleDocumentDeleted: Missing required event data');
        return;
      }

      try {
        // Get all places for the trip
        const places = await getPlaces(userId, tripId);
        if (!places || places.length === 0) {
          console.log('📍 [PlacesService] handleDocumentDeleted: No places found for trip');
          return;
        }

        // Process each place to remove document references
        for (const place of places) {
          if (!place.contentBlocks || place.contentBlocks.length === 0) continue;

          // Check if this place has document references for the deleted document
          const hasDocumentReference = place.contentBlocks.some(
            block => block.type === 'document_reference' && block.documentId === documentId
          );

          if (hasDocumentReference) {
            console.log('🗑️ [PlacesService] handleDocumentDeleted: Removing document references from place', {
              placeId: place.id,
              documentId
            });

            // Remove document reference blocks
            const updatedContentBlocks = removeDocumentReferenceBlocks(place.contentBlocks, [documentId]);
            
            // Update the place with new content blocks
            await updatePlace(userId, tripId, place.id, {
              contentBlocks: updatedContentBlocks,
              contentBlocksLastUpdated: new Date().toISOString()
            });
            
            console.log('✅ [PlacesService] handleDocumentDeleted: Successfully removed document references from place', {
              placeId: place.id,
              documentId
            });
          }
        }
      } catch (error) {
        console.error('❌ [PlacesService] handleDocumentDeleted: Error removing document references', {
          error: error.message,
          stack: error.stack
        });
      }
    });

    console.log('Places service event subscriptions initialized successfully');
  }
};

// Service exported above as named export
